import React, { useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { FlowBuilderContext } from 'pages/Flow/FlowBuilder/FlowBuilderContextProvider';
import { habuColors } from 'Theme';
import { useGetFlowNodeExportJobs } from 'hooks/flowExportAPI';


const FlowNodeExportJobs = ({ node }) => {
  const { crId, flowId } = useContext(FlowBuilderContext);

  const {
    data: exportJobsResponse = { jobs: [], count: 0 },
    isLoading,
    error,
    refetch,
  } = useGetFlowNodeExportJobs({ crId, flowId, nodeId: node.id });

  const exportJobs = exportJobsResponse.jobs || [];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
      case 'COMPLETED':
        return <SuccessIcon color='success' fontSize='small' />;
      case 'ERROR':
      case 'FAILED':
        return <ErrorIcon color='error' fontSize='small' />;
      case 'RUNNING':
      case 'PENDING':
        return <PendingIcon color='warning' fontSize='small' />;
      default:
        return <PendingIcon color='disabled' fontSize='small' />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
      case 'COMPLETED':
        return 'success';
      case 'ERROR':
      case 'FAILED':
        return 'error';
      case 'RUNNING':
        return 'warning';
      case 'PENDING':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant='h6' gutterBottom>
          Export Jobs
        </Typography>
        <LinearProgress />
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Loading export jobs...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant='h6' gutterBottom>
          Export Jobs
        </Typography>
        <Card variant='outlined' sx={{ borderColor: 'error.main' }}>
          <CardContent>
            <Stack direction='row' alignItems='center' spacing={1}>
              <ErrorIcon color='error' />
              <Typography variant='body2' color='error'>
                Failed to load export jobs. Please try again.
              </Typography>
              <IconButton size='small' onClick={refetch}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (exportJobs.length === 0) {
    return (
      <Box sx={{ py: 3 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
          <Typography variant='h6'>
            Export Jobs
          </Typography>
          <Tooltip title='Refresh'>
            <IconButton size='small' onClick={refetch}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Card variant='outlined'>
          <CardContent>
            <Stack alignItems='center' spacing={2} sx={{ py: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: habuColors.neutral[10],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DownloadIcon sx={{ fontSize: 32, color: habuColors.neutral[40] }} />
              </Box>
              <Typography variant='h6' color='text.secondary'>
                No Export Jobs Yet
              </Typography>
              <Typography variant='body2' color='text.secondary' textAlign='center'>
                Configure export destinations in the Settings tab to start creating export jobs.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h6'>
          Export Jobs (
          {exportJobs.length}
          )
        </Typography>
        <Tooltip title='Refresh'>
          <IconButton size='small' onClick={refetch}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <TableContainer component={Card} variant='outlined'>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ bgcolor: habuColors.neutral[5] }}>
              <TableCell>
                <Typography variant='subtitle2' fontWeight='bold'>
                  Destination
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2' fontWeight='bold'>
                  Status
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2' fontWeight='bold'>
                  Created
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='subtitle2' fontWeight='bold'>
                  Last Run
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exportJobs.map((job, index) => (
              <TableRow key={job.ID || index} hover>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography variant='body2' fontWeight='medium'>
                      {job.partnerAccountName || 'Unknown Partner'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {job.name || 'Export Job'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(job.status)}
                    label={job.status || 'PENDING'}
                    color={getStatusColor(job.status)}
                    size='small'
                    variant='outlined'
                  />
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {formatDate(job.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {job.lastRunTime ? formatDate(job.lastRunTime) : 'Never'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FlowNodeExportJobs;
